import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '@/hooks/useWebSocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { messages, isConnected } = useWebSocket(token);

  useEffect(() => {
    fetchUnreadCount();
    fetchRecentNotifications();
  }, []);

  // Handle real-time notifications
  useEffect(() => {
    const newNotifications = messages.filter(msg => msg.event === 'notification');
    if (newNotifications.length > 0) {
      const latest = newNotifications[newNotifications.length - 1];
      setRecentNotifications(prev => [latest.data, ...prev.slice(0, 4)]);
      setUnreadCount(prev => prev + 1);
    }
  }, [messages]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications/unread-count`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setUnreadCount(data.count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };

  const fetchRecentNotifications = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications?page=1&limit=5`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setRecentNotifications(data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch recent notifications');
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/admin/notifications');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-[hsl(351,96%,31%)] text-white text-xs"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <Badge variant="outline" className="bg-[hsl(351,96%,31%,0.1)] text-[hsl(351,96%,31%)]">
                {unreadCount} new
              </Badge>
            )}
          </div>
        </div>
        
        <ScrollArea className="h-[300px]">
          {recentNotifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-muted cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-[hsl(351,96%,31%,0.05)]' : ''
                  }`}
                  onClick={() => {
                    setOpen(false);
                    navigate('/admin/notifications');
                  }}
                >
                  <div className="flex gap-2">
                    {!notification.is_read && (
                      <div className="w-2 h-2 bg-[hsl(351,96%,31%)] rounded-full mt-1.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{notification.title}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(notification.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="p-3 border-t">
          <Button 
            variant="ghost" 
            className="w-full text-[hsl(351,96%,31%)] hover:bg-[hsl(351,96%,31%,0.1)]"
            onClick={handleViewAll}
          >
            View All Notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
